/**
 * Milestone Tracker Page
 * AIDLC Administrative Backend
 *
 * Displays milestone progress with a 6-gate pipeline visualization.
 * Each gate node is color-coded by status (passed/in_progress/pending/
 * failed/blocked) and clicking a gate shows details. Includes milestone
 * list, budget progress bar, and gate event timeline.
 *
 * Consumes GET /api/milestones and gate pass/fail endpoints.
 *
 * Personas: Executive, Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtTokens, fmtDate, fmtDateTime } from '../../utils/formatters';
import type {
  Milestone,
  MilestoneGate,
  GateStatus,
} from '../../services/orchestrationService';
import {
  getMilestones,
  passGate,
  failGate,
} from '../../services/orchestrationService';

/** Gate status visual configuration */
const gateStatusConfig: Record<GateStatus, {
  label: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconBg: string;
  connectorColor: string;
}> = {
  passed: {
    label: 'Passed',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-400',
    textColor: 'text-green-700',
    iconBg: 'bg-green-500',
    connectorColor: 'bg-green-400',
  },
  in_progress: {
    label: 'In Progress',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-400',
    textColor: 'text-blue-700',
    iconBg: 'bg-blue-500',
    connectorColor: 'bg-blue-300',
  },
  pending: {
    label: 'Pending',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    textColor: 'text-gray-500',
    iconBg: 'bg-gray-400',
    connectorColor: 'bg-gray-300',
  },
  failed: {
    label: 'Failed',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-400',
    textColor: 'text-red-700',
    iconBg: 'bg-red-500',
    connectorColor: 'bg-red-300',
  },
  blocked: {
    label: 'Blocked',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-400',
    textColor: 'text-orange-700',
    iconBg: 'bg-orange-500',
    connectorColor: 'bg-orange-300',
  },
};

/** Default 6-gate pipeline when backend doesn't provide gates */
const DEFAULT_GATES: MilestoneGate[] = [
  { id: 'g1', name: 'Backlog Ready', status: 'pending' },
  { id: 'g2', name: 'Budget Approved', status: 'pending' },
  { id: 'g3', name: 'Agents Spawned', status: 'pending' },
  { id: 'g4', name: 'Code Complete', status: 'pending' },
  { id: 'g5', name: 'DoD Passed', status: 'pending' },
  { id: 'g6', name: 'Shipped', status: 'pending' },
];

/** Gate short labels for the pipeline nodes */
const GATE_SHORT_LABELS: Record<string, string> = {
  g1: 'G1',
  g2: 'G2',
  g3: 'G3',
  g4: 'G4',
  g5: 'G5',
  g6: 'G6',
};

/** Milestone status badge config */
const milestoneStatusConfig: Record<string, { label: string; variant: 'success' | 'info' | 'neutral' | 'warning' }> = {
  planning: { label: 'Planning', variant: 'warning' },
  active: { label: 'Active', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

// ── Main Component ───────────────────────────────────────────────────

export function MilestoneTrackerPage() {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(null);
  const [selectedGate, setSelectedGate] = useState<MilestoneGate | null>(null);

  const loadMilestones = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getMilestones();
      setMilestones(data);
      // Auto-select first milestone if available
      if (data.length > 0 && !selectedMilestoneId) {
        setSelectedMilestoneId(data[0].id);
      }
    } catch (err) {
      console.error('[MilestoneTracker] Failed to load:', err);
      setError('Failed to load milestones. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedMilestoneId]);

  useEffect(() => {
    loadMilestones();
  }, []);

  const selectedMilestone = selectedMilestoneId
    ? milestones.find((m) => m.id === selectedMilestoneId) || null
    : null;

  const handleGateAction = async (milestoneId: string, gateId: string, action: 'pass' | 'fail', notes: string) => {
    try {
      if (action === 'pass') {
        await passGate(milestoneId, gateId, notes);
      } else {
        await failGate(milestoneId, gateId, notes);
      }
      setSelectedGate(null);
      await loadMilestones();
    } catch (err) {
      console.error('[MilestoneTracker] Gate action failed:', err);
      setError('Failed to update gate. Please try again.');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading milestones..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Milestones</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadMilestones}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (milestones.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-xl font-bold text-ldaja-black">Milestone Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track project milestones through the 6-gate delivery pipeline.
          </p>
        </div>
        <EmptyState
          title="No milestones found"
          description="No milestones have been created yet. Approve a proposal to generate a milestone."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Milestone Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track project milestones through the 6-gate delivery pipeline.
          </p>
        </div>
        <button
          onClick={loadMilestones}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Milestone Cards (if multiple) */}
      {milestones.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {milestones.map((ms) => (
            <MilestoneCard
              key={ms.id}
              milestone={ms}
              isSelected={ms.id === selectedMilestoneId}
              onClick={() => setSelectedMilestoneId(ms.id)}
            />
          ))}
        </div>
      )}

      {/* Selected Milestone Detail */}
      {selectedMilestone && (
        <>
          {/* Milestone Header (only show if single milestone) */}
          {milestones.length === 1 && (
            <MilestoneHeader milestone={selectedMilestone} />
          )}

          {/* Gate Pipeline Visualization */}
          <Card title="Gate Pipeline" subtitle="6-gate delivery process -- click a gate for details">
            <GatePipeline
              gates={selectedMilestone.gates?.length > 0 ? selectedMilestone.gates : DEFAULT_GATES}
              onGateClick={(gate) => setSelectedGate(gate)}
            />
          </Card>

          {/* Budget Progress */}
          <Card title="Budget Progress" subtitle="Token spend vs allocated budget">
            <BudgetProgress
              tokenBudget={selectedMilestone.tokenBudget}
              tokensSpent={selectedMilestone.tokensSpent}
            />
          </Card>

          {/* Milestone Details */}
          <Card title="Milestone Details">
            <MilestoneDetails milestone={selectedMilestone} />
          </Card>

          {/* Gate Event Timeline */}
          <Card title="Gate Event Timeline" subtitle="History of gate status changes">
            <GateTimeline
              gates={selectedMilestone.gates?.length > 0 ? selectedMilestone.gates : DEFAULT_GATES}
            />
          </Card>
        </>
      )}

      {/* Gate Detail Modal */}
      {selectedGate && selectedMilestone && (
        <GateDetailModal
          gate={selectedGate}
          milestoneId={selectedMilestone.id}
          onClose={() => setSelectedGate(null)}
          onAction={handleGateAction}
        />
      )}
    </div>
  );
}

// ── Milestone Card ───────────────────────────────────────────────────

function MilestoneCard({
  milestone,
  isSelected,
  onClick,
}: {
  milestone: Milestone;
  isSelected: boolean;
  onClick: () => void;
}) {
  const statusCfg = milestoneStatusConfig[milestone.status] || milestoneStatusConfig.active;
  const gates = milestone.gates?.length > 0 ? milestone.gates : DEFAULT_GATES;
  const passedCount = gates.filter((g) => g.status === 'passed').length;
  const spentPct = milestone.tokenBudget > 0
    ? Math.min(100, (milestone.tokensSpent / milestone.tokenBudget) * 100)
    : 0;

  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
        isSelected
          ? 'border-ldaja-blue ring-2 ring-ldaja-blue/20'
          : 'border-gray-200 hover:border-ldaja-blue/40'
      }`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-ldaja-black">{milestone.title}</h3>
        <Badge variant={statusCfg.variant} dot>
          {statusCfg.label}
        </Badge>
      </div>

      {/* Gate progress mini-bar */}
      <div className="flex items-center gap-1 mb-3">
        {gates.map((gate) => {
          const cfg = gateStatusConfig[gate.status] || gateStatusConfig.pending;
          return (
            <div
              key={gate.id}
              className={`h-2 flex-1 rounded-full ${cfg.iconBg}`}
              title={`${gate.name}: ${cfg.label}`}
            />
          );
        })}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>Gates: {passedCount}/{gates.length}</span>
        <span>Budget: {fmtTokens(milestone.tokensSpent)} / {fmtTokens(milestone.tokenBudget)}</span>
      </div>

      {/* Budget mini progress */}
      <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all ${
            spentPct > 90 ? 'bg-red-500' : spentPct > 70 ? 'bg-amber-500' : 'bg-green-500'
          }`}
          style={{ width: `${spentPct}%` }}
        />
      </div>
    </div>
  );
}

// ── Milestone Header (single-milestone display) ──────────────────────

function MilestoneHeader({ milestone }: { milestone: Milestone }) {
  const statusCfg = milestoneStatusConfig[milestone.status] || milestoneStatusConfig.active;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-ldaja-black">{milestone.title}</h2>
        <Badge variant={statusCfg.variant} dot>
          {statusCfg.label}
        </Badge>
      </div>
      {milestone.description && (
        <p className="text-sm text-gray-500 mt-1">{milestone.description}</p>
      )}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
        <span>Created: {fmtDate(milestone.createdAt)}</span>
        {milestone.projectId && <span>Project: {milestone.projectId}</span>}
        {milestone.proposalId && <span>Proposal: {milestone.proposalId}</span>}
      </div>
    </div>
  );
}

// ── Gate Pipeline Visualization ──────────────────────────────────────

function GatePipeline({
  gates,
  onGateClick,
}: {
  gates: MilestoneGate[];
  onGateClick: (gate: MilestoneGate) => void;
}) {
  return (
    <div className="py-4">
      {/* Desktop: horizontal pipeline */}
      <div className="hidden sm:flex items-center justify-between gap-0">
        {gates.map((gate, idx) => {
          const cfg = gateStatusConfig[gate.status] || gateStatusConfig.pending;
          const shortLabel = GATE_SHORT_LABELS[gate.id] || `G${idx + 1}`;
          const isLast = idx === gates.length - 1;

          return (
            <div key={gate.id} className="flex items-center flex-1">
              {/* Gate Node */}
              <button
                onClick={() => onGateClick(gate)}
                className={`
                  relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all
                  hover:shadow-md cursor-pointer min-w-[90px]
                  ${cfg.bgColor} ${cfg.borderColor}
                `}
                title={`${gate.name}: ${cfg.label}`}
              >
                {/* Gate Icon */}
                <div className={`w-10 h-10 rounded-full ${cfg.iconBg} flex items-center justify-center`}>
                  <GateIcon status={gate.status} />
                </div>

                {/* Gate Label */}
                <span className={`text-[10px] font-bold uppercase ${cfg.textColor}`}>
                  {shortLabel}
                </span>
                <span className="text-[10px] text-gray-500 text-center leading-tight max-w-[80px]">
                  {gate.name}
                </span>

                {/* Pulse animation for in_progress */}
                {gate.status === 'in_progress' && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
                  </span>
                )}
              </button>

              {/* Connector line between gates */}
              {!isLast && (
                <div className={`flex-1 h-1 mx-1 rounded-full ${cfg.connectorColor}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical pipeline */}
      <div className="sm:hidden space-y-2">
        {gates.map((gate, idx) => {
          const cfg = gateStatusConfig[gate.status] || gateStatusConfig.pending;
          const shortLabel = GATE_SHORT_LABELS[gate.id] || `G${idx + 1}`;
          const isLast = idx === gates.length - 1;

          return (
            <div key={gate.id}>
              <button
                onClick={() => onGateClick(gate)}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all
                  hover:shadow-md cursor-pointer
                  ${cfg.bgColor} ${cfg.borderColor}
                `}
              >
                <div className={`w-8 h-8 rounded-full ${cfg.iconBg} flex items-center justify-center shrink-0`}>
                  <GateIcon status={gate.status} />
                </div>
                <div className="text-left flex-1">
                  <span className={`text-xs font-bold ${cfg.textColor}`}>{shortLabel}: {gate.name}</span>
                  <span className={`block text-[10px] ${cfg.textColor}`}>{cfg.label}</span>
                </div>
                {gate.status === 'in_progress' && (
                  <span className="flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                  </span>
                )}
              </button>
              {!isLast && (
                <div className={`w-1 h-4 mx-auto rounded-full ${cfg.connectorColor}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Gate status icons */
function GateIcon({ status }: { status: GateStatus }) {
  const iconClass = 'w-5 h-5 text-white';

  switch (status) {
    case 'passed':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'in_progress':
      return (
        <svg className={`${iconClass} animate-spin`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      );
    case 'failed':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'blocked':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      );
    default: // pending
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
}

// ── Budget Progress ──────────────────────────────────────────────────

function BudgetProgress({ tokenBudget, tokensSpent }: { tokenBudget: number; tokensSpent: number }) {
  const pct = tokenBudget > 0 ? (tokensSpent / tokenBudget) * 100 : 0;
  const remaining = Math.max(0, tokenBudget - tokensSpent);
  const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-ldaja-blue';

  return (
    <div>
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Budget</p>
          <p className="text-lg font-bold text-ldaja-blue">{fmtTokens(tokenBudget)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Spent</p>
          <p className={`text-lg font-bold ${pct > 90 ? 'text-red-600' : 'text-gray-700'}`}>
            {fmtTokens(tokensSpent)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Remaining</p>
          <p className="text-lg font-bold text-green-600">{fmtTokens(remaining)}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor} flex items-center justify-center`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        >
          {pct > 15 && (
            <span className="text-white text-[10px] font-medium">{pct.toFixed(1)}%</span>
          )}
        </div>
      </div>
      {pct <= 15 && (
        <p className="text-xs text-gray-400 mt-1">{pct.toFixed(1)}% of budget used</p>
      )}
    </div>
  );
}

// ── Milestone Details ────────────────────────────────────────────────

function MilestoneDetails({ milestone }: { milestone: Milestone }) {
  return (
    <div className="space-y-4">
      {/* Description */}
      {milestone.description && (
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Description</p>
          <p className="text-sm text-gray-700">{milestone.description}</p>
        </div>
      )}

      {/* Linked Info */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-gray-400">Project</p>
          <p className="text-sm font-medium text-gray-700">{milestone.projectId || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Linked Proposal</p>
          <p className="text-sm font-medium text-ldaja-blue">{milestone.proposalId || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Created</p>
          <p className="text-sm font-medium text-gray-700">{fmtDate(milestone.createdAt)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Last Updated</p>
          <p className="text-sm font-medium text-gray-700">{fmtDate(milestone.updatedAt)}</p>
        </div>
      </div>

      {/* Assigned Backlog Items */}
      {milestone.backlogItems && milestone.backlogItems.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Assigned Backlog Items ({milestone.backlogItems.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {milestone.backlogItems.map((itemId) => (
              <span
                key={itemId}
                className="text-[10px] px-2 py-1 bg-gray-100 text-gray-600 rounded font-mono"
              >
                {itemId}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gate Event Timeline ──────────────────────────────────────────────

function GateTimeline({ gates }: { gates: MilestoneGate[] }) {
  // Only show gates that have some event (passed or failed)
  const eventGates = gates.filter((g) => g.passedAt || g.failedAt || g.status !== 'pending');

  if (eventGates.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">
        No gate events recorded yet.
      </p>
    );
  }

  return (
    <div className="space-y-0">
      {eventGates.map((gate, idx) => {
        const cfg = gateStatusConfig[gate.status] || gateStatusConfig.pending;
        const timestamp = gate.passedAt || gate.failedAt;
        const isLast = idx === eventGates.length - 1;

        return (
          <div key={gate.id} className="flex gap-3">
            {/* Timeline dot and line */}
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${cfg.iconBg} shrink-0 mt-1`} />
              {!isLast && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
            </div>

            {/* Event content */}
            <div className={`pb-4 ${isLast ? '' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{gate.name}</span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.bgColor} ${cfg.textColor}`}>
                  {cfg.label}
                </span>
              </div>
              {timestamp && (
                <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(timestamp)}</p>
              )}
              {gate.notes && (
                <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1.5">
                  {gate.notes}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Gate Detail Modal ────────────────────────────────────────────────

function GateDetailModal({
  gate,
  milestoneId,
  onClose,
  onAction,
}: {
  gate: MilestoneGate;
  milestoneId: string;
  onClose: () => void;
  onAction: (milestoneId: string, gateId: string, action: 'pass' | 'fail', notes: string) => void;
}) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cfg = gateStatusConfig[gate.status] || gateStatusConfig.pending;

  const handleAction = async (action: 'pass' | 'fail') => {
    setIsSubmitting(true);
    await onAction(milestoneId, gate.id, action, notes);
    setIsSubmitting(false);
  };

  const canTakeAction = gate.status === 'pending' || gate.status === 'in_progress' || gate.status === 'failed';

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Gate: ${gate.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          {canTakeAction && (
            <>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleAction('fail')}
                isLoading={isSubmitting}
              >
                Fail Gate
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleAction('pass')}
                isLoading={isSubmitting}
                className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50"
              >
                Pass Gate
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Status */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Current Status</p>
          <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded ${cfg.bgColor} ${cfg.textColor}`}>
            <span className={`w-2 h-2 rounded-full ${cfg.iconBg}`} />
            {cfg.label}
          </span>
        </div>

        {/* Timestamps */}
        {gate.passedAt && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Passed At</p>
            <p className="text-sm text-green-700">{fmtDateTime(gate.passedAt)}</p>
          </div>
        )}
        {gate.failedAt && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Failed At</p>
            <p className="text-sm text-red-700">{fmtDateTime(gate.failedAt)}</p>
          </div>
        )}

        {/* Existing notes */}
        {gate.notes && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Notes</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded p-2">{gate.notes}</p>
          </div>
        )}

        {/* Checklist */}
        {gate.checklist && gate.checklist.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Checklist</p>
            <ul className="space-y-1">
              {gate.checklist.map((entry, idx) => {
                const label = typeof entry === 'string' ? entry : entry.item;
                const done = typeof entry === 'object' && entry.done;
                return (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    {done ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    )}
                    <span className={done ? 'line-through text-gray-400' : ''}>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Notes input for action */}
        {canTakeAction && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Add Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="Add notes about this gate..."
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
